import * as React from "react";
import { AppRole } from "../../api/types";
import { loadSettings, saveSettings } from "../settings/settingsStore";

interface RoleContextType {
  activeRole: AppRole;
  validRoles: AppRole[];
  setActiveRole: (role: AppRole) => void;
}

const RoleContext = React.createContext<RoleContextType | null>(null);

const STORAGE_KEY = "activeRole";

export const RoleProvider: React.FC<{
  validRoles: AppRole[];
  children: React.ReactNode;
}> = ({ validRoles, children }) => {
  const [activeRole, setActiveRoleState] = React.useState<AppRole>(() => {
    const saved = loadSettings<string>(STORAGE_KEY);
    if (saved && validRoles.includes(saved as AppRole)) {
      return saved as AppRole;
    }
    return validRoles[0];
  });

  const setActiveRole = React.useCallback(
    (role: AppRole) => {
      if (validRoles.includes(role)) {
        setActiveRoleState(role);
        saveSettings(STORAGE_KEY, role);
      }
    },
    [validRoles]
  );

  const value = React.useMemo(
    () => ({ activeRole, validRoles, setActiveRole }),
    [activeRole, validRoles, setActiveRole]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export function useRole(): RoleContextType {
  const ctx = React.useContext(RoleContext);
  if (!ctx) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return ctx;
}
